import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { AuthenticationRouteNames } from '@core/presentation';
import { UsersServices } from '@modules/company-manager/users/domain';
import { NotificationsService } from '@geekbears/ngx-gb-ui-kit';
import { Store } from '@ngrx/store';
import {
  AuthenticationDataModel,
  AuthenticationNgRxActions,
} from '@geekbears/gb-ngrx-auth-basic';
import { take } from 'rxjs';
import { AnalyticsService, appAuthenticatedUserSelector, NavigationService } from '@core/application';
import { passwordValidator, getPasswordValidationMessages } from '@utils/password.validator';

@Component({
  selector: 'app-signup-info',
  templateUrl: './signup-info.page.html',
  styleUrls: ['./signup-info.page.scss'],
})
export class SignupInfoPage implements OnInit {
  form!: FormGroup;
  isLoading = false;
  role: string | null = null;
  isAdmin = false;
  showPassword = false;
  showConfirmPassword = false;
  user: any = null;

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private usersService: UsersServices,
    private notifications: NotificationsService,
    private store: Store,
    private navigationService: NavigationService,
    private analytics: AnalyticsService
  ) {}

  ngOnInit(): void {
    this.role = localStorage.getItem('signup_role');
    
    if (this.role) {
      this.isAdmin = this.role === 'company-administrator';
      this.initForm();
    } else {
      // If no role in localStorage, check store (redirection from guard)
      this.store.select(appAuthenticatedUserSelector).pipe(take(1)).subscribe(user => {
        if (user) {
          this.user = user;
          this.role = user.isManager ? 'company-administrator' : 'requester';
          this.isAdmin = this.role === 'company-administrator';
          this.initForm();
        } else {
          this.router.navigate(['/authentication', AuthenticationRouteNames.signIn]);
        }
      });
    }
  }

  initForm() {
    const controls: any = {
      firstName: ['', Validators.required],
      lastName: ['', Validators.required],
    };

    if (this.isAdmin) {
      controls.companyName = ['', Validators.required];
    } else {
      controls.password = ['', [Validators.required, passwordValidator()]];
      controls.confirmPassword = ['', [Validators.required, passwordValidator()]];
    }

    this.form = this.fb.group(controls, {
      validators: !this.isAdmin ? this.passwordMatchValidator : null
    });
  }

  getPasswordErrorMessage(controlName: string): string {
    const control = this.form?.get(controlName);
    if (!control || !control.touched || control.valid) {
      return '';
    }
    if (control.errors?.['required']) {
      return 'Please enter your password';
    }
    if (control.errors?.['passwordStrength']?.firstMessage) {
      return control.errors['passwordStrength'].firstMessage;
    }
    const msgs = getPasswordValidationMessages(control.value || '');
    return msgs.length > 0 ? msgs[0] : '';
  }

  passwordMatchValidator(g: FormGroup) {
    return g.get('password')?.value === g.get('confirmPassword')?.value
      ? null : { 'mismatch': true };
  }

  goBack() {
    this.navigationService.back();
  }

  onSubmit() {
    if (this.form.valid) {
      this.isLoading = true;
      const data: any = {
        firstName: this.form.value.firstName,
        lastName: this.form.value.lastName,
        deviceInfo: {
          id: this.generateDeviceId(),
          name: this.getDeviceName(),
          // userAgent: 'Check Plus Mobile App'
        },
        email: localStorage.getItem('signup_email') || this.user?.email,
        companyName: this.isAdmin ? this.form.value.companyName : undefined,
        role: this.role || (this.isAdmin ? 'company-administrator' : 'requester'),
        password: this.isAdmin ? localStorage.getItem('signup_password') : this.form.value.password,
        confirmPassword: this.isAdmin ? localStorage.getItem('signup_confirm_password') : this.form.value.confirmPassword,
        inviteCode: !this.isAdmin ? localStorage.getItem('signup_invite_code') : undefined
      };

      // Remove undefined properties
      Object.keys(data).forEach(key => data[key] === undefined && delete data[key]);

      this.usersService.signUpV3(data).subscribe({
        next: (res) => {
          this.isLoading = false;

          // Backend confirmed account creation.
          this.analytics.trackSignUp();
          
          // Some backends return tokens at top level and user under data.user.
          const user = res?.data?.user ?? res?.data ?? res?.user;
          const authToken = res?.authToken ?? res?.data?.authToken ?? res?.data?.token;
          const refreshToken = res?.refreshToken ?? res?.data?.refreshToken;
          const userId = user?.id ?? user?._id ?? res?.data?.id;

          if (user) {
            localStorage.setItem('s_user', JSON.stringify(user));
          }

          if (!this.isAdmin) {
            if (authToken) {
              localStorage.setItem('auth_token', authToken);
            }

            if (refreshToken) {
              localStorage.setItem('refresh_token', refreshToken);
            }

            // Dispatch auth session to populate authenticated user in store
            if (authToken && userId) {
              this.store.dispatch(
                AuthenticationNgRxActions.setAuthSession({
                  data: new AuthenticationDataModel({
                    id: userId,
                    token: authToken,
                    refreshToken: refreshToken,
                  }),
                })
              );
            }
          }

          this.notifications.success('Signup successful', { duration: 3000, icon: 'check-circle' });
          
          if (this.isAdmin) {
            localStorage.removeItem('signup_password');
            localStorage.removeItem('signup_confirm_password');
            this.router.navigate(['/authentication', AuthenticationRouteNames.thankYou]);
          } else {
            localStorage.removeItem('signup_invite_code');
            this.router.navigateByUrl('/dashboard');
          }
        },
        error: (err) => {
          this.isLoading = false;
          this.notifications.error(err.error?.message || 'Error completing signup', { duration: 5000, icon: 'alert-outline' });
        }
      });
    }
  }


   private generateDeviceId(): string {
    // Generate or retrieve a unique device ID
    let deviceId = localStorage.getItem('deviceId');
    if (!deviceId) {
      deviceId = 'device-' + Math.random().toString(36).substring(2, 11);
      localStorage.setItem('deviceId', deviceId);
    }
    return deviceId;
  }

  private getDeviceName(): string {
    // Get browser/device name from user agent
    const userAgent = navigator.userAgent;
    if (userAgent.includes('Chrome')) return 'Chrome Browser';
    if (userAgent.includes('Safari')) return 'Safari Browser';
    if (userAgent.includes('Firefox')) return 'Firefox Browser';
    if (userAgent.includes('MSIE') || userAgent.includes('Trident')) return 'Internet Explorer';
    return 'Unknown Device';
  }
}
